import React, { useState, useEffect } from "react";
import { Icon } from '@iconify/react/dist/iconify.js';
import toast from "react-hot-toast";
import { getPopupApi, createPopupApi, updatePopupApi, deletePopupApi, ImageUrl } from "../../helper/apiHelper";

const PopupComponent = () => {
    const [advertisements, setAdvertisements] = useState([]);
    const [saveLoading, setSaveLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [newAd, setNewAd] = useState({
        id: '',
        image: '',
        redirectUrl: '',
        startDate: '',
        endDate: '',
        activeTime: ''
    });

    // Fetch popups on component mount
    useEffect(() => {
        fetchPopups();
    }, []);

    const fetchPopups = async () => {
        setIsLoading(true);
        try {
            const response = await getPopupApi();
            if (response.data) {
                // Map API response to component state
                const mappedData = response.data.map(item => ({
                    id: item.id,
                    image: `${ImageUrl}/${item.image}`,
                    redirectUrl: item.redirectionUrl,
                    startDate: item.startDate ? item.startDate.split('T')[0] : '',
                    endDate: item.endDate ? item.endDate.split('T')[0] : '',
                    activeTime: item.activeSecond
                }));
                setAdvertisements(mappedData);
            }
        } catch (error) {
            toast.error(error || "Failed to fetch popups");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        // Validation
        if (!newAd.redirectUrl) {
            toast.error("Please enter redirection URL", { id: "redirect-url" });
            return;
        }
        if (!newAd.startDate) {
            toast.error("Please select start date", { id: "start-date" });
            return;
        }
        if (!newAd.endDate) {
            toast.error("Please select end date", { id: "end-date" });
            return;
        }
        if (!newAd.activeTime) {
            toast.error("Please enter active time", { id: "active-time" });
            return;
        }
        if (!isEditing && !imageFile) {
            toast.error("Please select an image", { id: "image-required" });
            return;
        }

        setSaveLoading(true);

        try {
            const formData = new FormData();
            formData.append("redirectionUrl", newAd.redirectUrl);
            formData.append("startDate", new Date(newAd.startDate).toISOString());
            formData.append("endDate", new Date(newAd.endDate).toISOString());
            formData.append("activeSecond", newAd.activeTime);

            if (imageFile) {
                formData.append("image", imageFile);
            }

            if (isEditing) {
                await updatePopupApi(formData, newAd.id);
                toast.success("Popup updated successfully", { id: "popup-success" });
            } else {
                await createPopupApi(formData);
                toast.success("Popup added successfully", { id: "popup-success" });
            }

            await fetchPopups();

            setShowModal(false);
            setNewAd({
                id: "",
                image: "",
                redirectUrl: "",
                startDate: "",
                endDate: "",
                activeTime: "",
            });
            setImageFile(null);
            setIsEditing(false);

        } catch (error) {
            toast.error(
                error || "Failed to save popup",
                { id: "popup-error" }
            );
        } finally {
            setSaveLoading(false);
        }
    };


    const handleEdit = (item) => {
        setIsEditing(true);
        setNewAd(item);
        setImageFile(null); // Reset image file for editing
        setShowModal(true);
    };

    const handleDelete = (id) => {
        setDeleteId(id);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        setIsLoading(true);
        try {
            await deletePopupApi(deleteId);
            toast.success("Popup deleted successfully");
            await fetchPopups();
            setShowDeleteModal(false);
            setDeleteId(null);
        } catch (error) {
            toast.error(error || "Failed to delete popup");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="card h-100 p-0 radius-12 overflow-hidden gallery-scale">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between gap-2" style={{ flexWrap: "wrap" }}>
                <h6 className="text-lg fw-semibold mb-0">Popup Configuration</h6>
                <button
                    type="button"
                    className="btn btn-primary-600 radius-8 px-20 py-11 d-flex align-items-center gap-2"
                    onClick={() => {
                        setIsEditing(false);
                        setNewAd({
                            id: '',
                            image: '',
                            redirectUrl: '',
                            startDate: '',
                            endDate: '',
                            activeTime: ''
                        });
                        setShowModal(true);
                    }}
                >
                    <Icon icon="ic:baseline-plus" className="text-xl" />
                    Add Offer Popup
                </button>
            </div>


            <div className="card-body p-24">
                {isLoading ? (
                    <div className="text-center py-40">
                        <div className="spinner-border text-primary" role="status" />
                    </div>
                ) : advertisements.length === 0 ? (
                    <div className="text-center py-40 text-muted fw-medium">
                        No popup found.
                    </div>
                ) : (
                    <div className="row gy-4">
                        {advertisements.map((item) => (
                            <div className="col-xxl-4 col-md-4 col-sm-6" key={item.id}>
                                <div className="hover-scale-img border radius-16 overflow-hidden p-8 position-relative group">
                                    <div className="radius-12 overflow-hidden position-relative">
                                        <img
                                            src={item.image}
                                            alt=""
                                            className="hover-scale-img__img w-100 h-100 object-fit-cover"
                                            style={{ maxHeight: '400px' }}
                                        />
                                        <div className="position-absolute top-50 start-50 translate-middle d-flex gap-2">
                                            <button
                                                onClick={() => handleEdit(item)}
                                                className="w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle bg-white text-primary-600 shadow-sm"
                                            >
                                                <Icon icon="lucide:edit" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(item.id)}
                                                className="w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle bg-white text-danger-600 shadow-sm"
                                            >
                                                <Icon icon="fluent:delete-24-regular" />
                                            </button>
                                        </div>
                                    </div>
                                    {item.redirectUrl && (
                                        <div className="mt-2 text-center">
                                            <small className="text-muted text-truncate d-block" style={{ maxWidth: '100%' }}>
                                                {item.redirectUrl}
                                            </small>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <>
                    <div className="modal fade show" style={{ display: 'block' }}>
                        <div className="modal-dialog modal-md modal-dialog-centered" style={{ zIndex: 11122 }}>
                            <div className="modal-content radius-16 bg-base">
                                <div className="modal-header py-16 px-24 border-bottom">
                                    <h1 className="modal-title fs-5">{isEditing ? "Edit Offer Popup" : "Add Offer Popup"}</h1>
                                    <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
                                </div>
                                <div className="modal-body p-24">
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Redirection URL</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter redirection URL"
                                            value={newAd.redirectUrl}
                                            onChange={(e) => setNewAd({ ...newAd, redirectUrl: e.target.value })}
                                        />
                                    </div>
                                    <div className="row">
                                        <div className="col-6 mb-3">
                                            <label className="form-label fw-semibold">Start Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={newAd.startDate}
                                                onChange={(e) => setNewAd({ ...newAd, startDate: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-6 mb-3">
                                            <label className="form-label fw-semibold">End Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={newAd.endDate}
                                                onChange={(e) => setNewAd({ ...newAd, endDate: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Active Time (Seconds)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            placeholder="Enter active active time in seconds"
                                            value={newAd.activeTime}
                                            onChange={(e) => setNewAd({ ...newAd, activeTime: e.target.value })}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Upload Image <span className="text-muted fs-12" style={{ fontWeight: "normal" }} >(Recommended Size: 500px x 500px)</span></label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            accept="image/*"
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (!file) return;

                                                const reader = new FileReader();
                                                const img = new Image();

                                                reader.onload = (event) => {
                                                    img.src = event.target.result;
                                                };

                                                img.onload = () => {
                                                    const TARGET_SIZE = 500;

                                                    const canvas = document.createElement("canvas");
                                                    canvas.width = TARGET_SIZE;
                                                    canvas.height = TARGET_SIZE;

                                                    const ctx = canvas.getContext("2d");

                                                    // Maintain aspect ratio & center crop
                                                    const scale = Math.max(
                                                        TARGET_SIZE / img.width,
                                                        TARGET_SIZE / img.height
                                                    );

                                                    const x = (TARGET_SIZE - img.width * scale) / 2;
                                                    const y = (TARGET_SIZE - img.height * scale) / 2;

                                                    ctx.drawImage(
                                                        img,
                                                        x,
                                                        y,
                                                        img.width * scale,
                                                        img.height * scale
                                                    );

                                                    // Convert canvas to real file
                                                    canvas.toBlob(
                                                        (blob) => {
                                                            const resizedFile = new File(
                                                                [blob],
                                                                file.name,
                                                                { type: "image/jpeg", lastModified: Date.now() }
                                                            );

                                                            // ✅ This is the resized image for upload
                                                            setImageFile(resizedFile);

                                                            // ✅ Preview uses resized image
                                                            const previewUrl = URL.createObjectURL(resizedFile);
                                                            setNewAd((prev) => ({
                                                                ...prev,
                                                                image: previewUrl
                                                            }));
                                                        },
                                                        "image/jpeg",
                                                        0.9
                                                    );
                                                };

                                                reader.readAsDataURL(file);
                                            }}
                                        />
                                    </div>
                                    {newAd.image && (
                                        <div className="mb-3">
                                            <div
                                                style={{
                                                    width: "100%",
                                                    maxWidth: "300px",
                                                    aspectRatio: "1 / 1",
                                                    overflow: "hidden",
                                                    borderRadius: "8px",
                                                    border: "1px solid #e5e7eb"
                                                }}
                                            >
                                                <img
                                                    src={newAd.image}
                                                    alt="Preview"
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover"
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="d-flex align-items-center justify-content-end gap-3 mt-24">
                                        <button
                                            type="button"
                                            className="btn btn-secondary border border-secondary-600 text-md px-24 py-10 radius-8"
                                            onClick={() => setShowModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary border border-primary-600 text-md px-32 py-10 radius-8"
                                            onClick={handleSave}
                                            disabled={saveLoading}
                                        >
                                            {isEditing ? 'Update' : 'Save'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
                    </div>
                </>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <>
                    <div className="modal fade show" style={{ display: 'block' }}>
                        <div className="modal-dialog modal-dialog-centered" style={{ zIndex: 11122 }}>
                            <div className="modal-content radius-16 bg-base">
                                <div className="modal-header py-16 px-24 border-bottom">
                                    <h1 className="modal-title fs-5 text-danger-600">
                                        {/* <Icon icon="mdi:alert-circle-outline" className="me-2" /> */}
                                        Confirm Delete
                                    </h1>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => {
                                            setShowDeleteModal(false);
                                            setDeleteId(null);
                                        }}
                                    />
                                </div>
                                <div className="modal-body p-24">
                                    <p className="text-secondary-light mb-0">
                                        Are you sure you want to delete this popup? This action cannot be undone.
                                    </p>
                                </div>
                                <div className="modal-footer py-16 px-24 border-top">
                                    <button
                                        type="button"
                                        className="btn btn-secondary border border-secondary-600 text-md px-24 py-10 radius-8"
                                        onClick={() => {
                                            setShowDeleteModal(false);
                                            setDeleteId(null);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger border border-danger-600 text-md px-24 py-10 radius-8"
                                        onClick={confirmDelete}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Deleting...' : 'Delete'}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
                    </div>
                </>
            )}
        </div>
    );
};

export default PopupComponent;
